import { useEffect, useState } from "react";

/** Exact audience-whitelisted timer event; never receives a persisted heat row. */
export interface HeatTimerEvent {
  type: "timer";
  heat_id: string;
  area_id: string | null;
  mode: "heat" | "pass";
  heat_rider_id: string | null;
  pass_number: number;
  duration_s: number;
  last_event: {
    kind:
      | "start"
      | "pause"
      | "resume"
      | "reset"
      | "adjust"
      | "skip"
      | "stop_on_fall"
      | "pull"
      | "next_pass";
    at: string;
    delta_s: number | null;
  } | null;
  remaining_s_at_server_time: number;
  server_time: string;
  running: boolean;
  reverse_direction: boolean;
}

export interface ClockSample {
  /** Server UTC milliseconds minus the device UTC milliseconds. */
  offsetMs: number;
  roundTripMs: number;
}

export function estimateClockSkew(
  serverTime: string,
  sentAt: number,
  receivedAt: number,
): ClockSample {
  return {
    offsetMs: Date.parse(serverTime) - (sentAt + receivedAt) / 2,
    roundTripMs: Math.max(0, receivedAt - sentAt),
  };
}

export function timerRemaining(
  event: HeatTimerEvent,
  running: boolean,
  nowMs: number,
  offsetMs: number,
): number {
  const elapsed = running
    ? Math.max(0, (nowMs + offsetMs - Date.parse(event.server_time)) / 1000)
    : 0;
  return Math.max(0, event.remaining_s_at_server_time - elapsed);
}

export function timerRunning(event: HeatTimerEvent): boolean {
  return event.running;
}

/** Continues locally through offline gaps; server snapshots correct reconnection. */
export function useHeatTimer(
  event: HeatTimerEvent | null,
  running = event?.running ?? false,
  offsetMs = 0,
) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!event || !running) return;
    const interval = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(interval);
  }, [event, running]);
  const remaining = event ? timerRemaining(event, running, now, offsetMs) : 0;
  return { remaining, display: formatTimer(remaining), running };
}

export function formatTimer(seconds: number): string {
  const whole = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(whole / 60)
    .toString()
    .padStart(2, "0")}:${(whole % 60).toString().padStart(2, "0")}`;
}
